import React, { useContext, useEffect, useRef, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import {
  GlobalDispatchContext,
  GlobalStateContext,
} from "../context/global-context-provider";
import {
  formatPrice,
  getKlaviyoImage,
  getSHA256Hash,
  updateDataLayer,
  uuidv4,
} from "../utils/helpers";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { graphql, Link, navigate, useStaticQuery } from "gatsby";
import Paragraph from "./paragraph";
import Image from "./image";
import { _ } from "../utils/i18n";
import Button from "./button";

const CheckoutContainer = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    width: 100%;
    display: flex;
    align-self: stretch;
    flex-flow: row;
  }
`;

const DinteroCheckoutContainer = styled.div`
  max-width: 700px;
  flex: 1 1 0;
`;

const SummaryWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    display: flex;
    margin-top: 32px;
    width: 455px;
    flex: 0 0 500px;
    -moz-box-flex: 0;
  }

  -moz-box-align: stretch;
  align-items: stretch;
  flex-direction: column;
  color: rgb(23, 23, 23);
`;

const SummaryTitle = styled.h2`
  font-size: 18px;
  font-weight: 500;
  margin-top: 0;
`;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ProductWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  padding: 1rem 2rem;
  gap: clamp(1rem, 2vw, 1.5rem);
  border-bottom: 1px solid rgb(163, 163, 163);
`;
const StyledImage = styled(Image)`
  max-width: 110px;
  max-height: 110px;
  background-color: var(--color-white);

  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    min-width: 110px;
    min-height: 110px;
    object-fit: contain !important;
  }
  border: 1px solid var(--color-grey);
  border-radius: var(--border-radius);
`;
const MiddleWrapper = styled.div`
  display: flex;
  flex-flow: column;
  height: 100%;
  justify-content: space-between;
  flex-basis: 75%;
  align-items: flex-start;
  min-height: 110px;
`;

const NameWrapper = styled.div`
  width: 100%;
`;
const StyledLink = styled(Link)`
  width: 100%;
  font-weight: 500;
  font-size: 14px;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: clamp(0.6rem, 1.1vw, 0.6rem);
  }
  display: block;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const StyledParagraph = styled(Paragraph)`
  font-size: 0.7rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    font-size: 0.6rem;
  }
`;

const RightWrapper = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: space-between;
`;
const Price = styled.div`
  display: grid;
  padding-block-end: 0.525rem;
  & p {
    margin: 0;
    text-align: right;
  }
`;

const LoadingIcon = styled(FontAwesomeIcon)`
  font-size: 5rem;
  color: var(--color-black);
  opacity: 0.8;
`;

const LoadingContainer = styled.div`
  height: 100px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Badge = styled.div`
  display: inline-block;
  color: rgb(255, 255, 255);
  background: rgb(23, 23, 23);
  padding: 4px 10px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  margin-right: 8px;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
`;

const SkuAndBadgeWrapper = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
`;

const NoItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h2 {
    width: fit-content;
  }
`;

const DinteroCheckout = () => {
  const { wpProductCategory } = useStaticQuery(graphql`
    query {
      wpProductCategory(crbMasterCategory: { eq: true }) {
        uri
      }
    }
  `);

  const [createDinteroSession] = useMutation(CREATE_DINTERO_CHECKOUT_SESSION);
  const [updateDinteroSession] = useMutation(UPDATE_DINTERO_CHECKOUT_SESSION);
  const [sendMetricKlaviyo] = useMutation(Send_Metric_Klaviyo);
  const {
    user,
    isLoggedIn,
    cart,
    cartSyncInProgress,
    cartKlaviyoId,
    cartRevision,
  } = useContext(GlobalStateContext);
  const dispatch = useContext(GlobalDispatchContext);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRefreshSession, setShouldRefreshSession] = useState(false);
  const [shouldCheckUpdate, setShouldCheckUpdate] = useState(false);

  const gtmEvent = async (event, transactionId, cartData) => {
    updateDataLayer(async () => {
      let dataLayerObject = {
        event: event,
        ecommerce: {
          logged_in: isLoggedIn ?? false,
          email: user ? await getSHA256Hash(user?.email) : null,
          currency: cartData?.[0]?.currency,
          value: cartData.reduce(
            (acc, item) => acc + item.priceRaw * item.quantity,
            0
          ),
          items: cartData.map((item) => ({
            item_id: item?.id,
            item_name: item?.name,
            item_category: item?.category,
            item_brand: item?.brand,
            price: item?.priceRaw,
            quantity: item?.quantity,
          })),
        },
      };

      if (event === "purchase") {
        dataLayerObject.ecommerce.transaction_id = transactionId;
      }

      window.dataLayer.push(dataLayerObject);
    });
  };

  const sendKlaviyoPurchaseEvent = (cartData) => {
    if (user?.email) {
      sendMetricKlaviyo({
        variables: {
          unique_id: uuidv4(),
          email: user.email,
          metric: "Placed Order",
          products: cartData.slice(0, 5).map((item) => ({
            ...item,
            image: getKlaviyoImage(item.image),
          })),
        },
      });
    }
  };

  useEffect(() => {
    if (user?.email) {
      sendMetricKlaviyo({
        variables: {
          unique_id: cartKlaviyoId,
          email: user.email,
          metric: "Started Checkout",
          products: cart.slice(0, 5).map((item) => ({
            ...item,
            image: getKlaviyoImage(item.image),
          })),
        },
      });
    }
  }, [user]);

  const checkoutContainerRef = useRef(null);
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);
  const [isSession, setIsSession] = useState(false);
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    const loadCheckout = async () => {
      if (
        checkoutContainerRef.current &&
        !checkoutLoaded &&
        !cartSyncInProgress &&
        cartRevision > 0
      ) {
        setCheckoutLoaded(true);
        const { embed } = await import("@dintero/checkout-web-sdk");
        const dinteroSession = localStorage.getItem("dintero_session");
        localStorage.setItem("dintero_cart_revision", cartRevision);

        const commonEmbedProperties = {
          container: checkoutContainerRef.current,
          language: "no",
          popOut: true,
          onSession: function () {
            setIsSession(true);
          },
          onPayment: async function (e) {
            const cartCopy = [...cart];
            await gtmEvent("purchase", e.transaction_id, cartCopy);
            sendKlaviyoPurchaseEvent(cartCopy);
            dispatch({ type: "CLEAR_CART" });
            localStorage.removeItem("dintero_session");
            navigate(`/thank-you?transactionId=${e.transaction_id}`);
          },
          onPaymentError: function () {
            window.location.reload();
          },
          onSessionCancel: function (checkout) {
            checkout.destroy();
          },
          onSessionNotFound: function (event, checkout) {
            localStorage.removeItem("dintero_session");
            checkout.destroy();
            window.location.reload();
          },
          onSessionLocked: function (event, checkout, callback) {
            callback();
          },
          onValidateSession: function (event, checkout, callback) {
            callback({
              success: true,
              clientValidationError: undefined,
            });
          },
        };

        if (!dinteroSession) {
          createDinteroSession()
            .then(({ data }) => {
              localStorage.setItem(
                "dintero_session",
                data.createDinteroSession.sessionId
              );
              gtmEvent("begin_checkout");
              embed({
                ...commonEmbedProperties,
                sid: data.createDinteroSession.sessionId,
              }).then((checkout) => {
                setCheckout(checkout);
                setIsLoading(false);
              });
            })
            .catch(() => window.localStorage.removeItem("woo-session"));
        } else {
          embed({
            ...commonEmbedProperties,
            sid: dinteroSession,
          }).then((checkout) => {
            setCheckout(checkout);
            setIsLoading(false);
            setShouldCheckUpdate(true);
          });
        }
      }
    };

    loadCheckout();
  }, [checkoutContainerRef.current, cartSyncInProgress, cartRevision]);

  useEffect(() => {
    if (!checkout || isLoading || cart.length === 0) {
      return;
    }

    if (cartSyncInProgress) {
      setShouldRefreshSession(true);
    } else if (!cartSyncInProgress && shouldRefreshSession) {
      setShouldRefreshSession(false);
      checkout
        .lockSession()
        .then(() => {
          const dinteroSession = localStorage.getItem("dintero_session");
          updateDinteroSession({
            variables: {
              sessionId: dinteroSession,
            },
          })
            .then(() => {
              setTimeout(() => {
                checkout.refreshSession();
              }, 500);
            })
            .catch((error) => {
              console.error(error);
            });
        })
        .catch(function (sessionLockFailedEvent) {
          console.error(sessionLockFailedEvent);
        });
    }
  }, [cartSyncInProgress, checkout, isLoading, shouldRefreshSession, cart]);

  useEffect(() => {
    if (shouldCheckUpdate && isSession) {
      const dinteroCartRevision = localStorage.getItem("dintero_cart_revision");
      if (dinteroCartRevision !== cartRevision) {
        setShouldRefreshSession(true);
      }
    }
  }, [shouldCheckUpdate, isSession]);

  if (cart.length === 0) {
    return (
      <NoItemsWrapper>
        <SummaryTitle>{_("Handlekurven din er tom")} </SummaryTitle>
        {wpProductCategory && (
          <Button
            url={wpProductCategory.uri}
            label={_("Begynn å handle")}
            variant="filled"
            padding="0.5rem 0.9rem"
          />
        )}
      </NoItemsWrapper>
    );
  }

  return (
    <>
      {isLoading && (
        <LoadingContainer>
          <LoadingIcon icon={faSpinner} className="fa-spin" />
        </LoadingContainer>
      )}
      <CheckoutContainer>
        <DinteroCheckoutContainer
          id="checkout-container"
          ref={checkoutContainerRef}
        />
        {!isLoading && (
          <SummaryWrapper>
            <SummaryTitle>
              {_(`Handlekurv (${cart.length} produkter)`)}
            </SummaryTitle>
            {cart && cart.length > 0 && (
              <Wrapper>
                {cart.map((product) => (
                  <ProductWrapper key={product.id}>
                    <StyledImage image={product.image} />
                    <MiddleWrapper>
                      <NameWrapper>
                        <StyledLink to={product.uri}>{product.name}</StyledLink>
                        <SkuAndBadgeWrapper>
                          {product.sku && (
                            <StyledParagraph content={product?.sku} />
                          )}
                          <Badge>{`x ${product.quantity}`}</Badge>
                        </SkuAndBadgeWrapper>
                      </NameWrapper>
                    </MiddleWrapper>
                    <RightWrapper>
                      <Price>
                        <Paragraph
                          content={formatPrice(
                            (product.priceRaw * product.quantity).toString()
                          )}
                          fontSize="clamp(0.9rem, 2vw, 1rem)"
                          innerStyle={{ fontWeight: "500" }}
                        />
                      </Price>
                    </RightWrapper>
                  </ProductWrapper>
                ))}
              </Wrapper>
            )}
          </SummaryWrapper>
        )}
      </CheckoutContainer>
    </>
  );
};

export default DinteroCheckout;

const CREATE_DINTERO_CHECKOUT_SESSION = gql`
  mutation CreateDinteroSession {
    createDinteroSession(input: {}) {
      sessionId
    }
  }
`;

const UPDATE_DINTERO_CHECKOUT_SESSION = gql`
  mutation UpdateDinteroSession($sessionId: String!) {
    updateDinteroSession(input: { sessionId: $sessionId }) {
      clientMutationId
    }
  }
`;

export const Send_Metric_Klaviyo = gql`
  mutation sendMetricKlaviyo(
    $unique_id: String!
    $email: String!
    $metric: String!
    $products: [KlaviyoProduct]
  ) {
    sendMetricKlaviyo(
      input: {
        unique_id: $unique_id
        email: $email
        metric: $metric
        products: $products
      }
    ) {
      status
    }
  }
`;
